import { useProjectParams } from '@hooks/useProjectParams';
import { KebabTreeProject } from 'components/KebabTreeProject';
import { HEADER_HEIGHT } from 'components/Layout/Master';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';

import type { CSSProperties } from 'react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CalculationTree } from './CalculationTree';
import { NavItems } from './constants';
import { ContractTree } from './ContractTree';
import { ProjectTreeLeaf } from './ProjectTreeLeaf';
import { useCheckActiveTreeItem } from './useCheckActiveTreeItem';

import './ProjectTree.css';

const getContractNavItemUrl = ({
    baseUrl,
    itemId,
    navLinkId,
}: {
    baseUrl: string;
    itemId: number;
    navLinkId: string;
}) => {
    const urlMap: { [key: string]: string } = {
        [NavItems.ContractChange]: '/contract/agreements',
        [NavItems.PriceListUER]: '/contract/prices',
        [NavItems.PriceListMTR]: '/contract/mtr',
        [NavItems.PriceListRatio]: '/contract/factors',
        [NavItems.ContractOther]: '/contract/others',
        [NavItems.ContractActsKC3]: `/contract/actsKC3`,
        [NavItems.ContractStructure]: '/contract/structure',
        [NavItems.ContractObject]: '/contract/structure',
        [NavItems.ContractCalculation]: `/order/${itemId}`,
        [NavItems.WorkOrderActsKC2]: `/order/${itemId}/actsKC2List`,
        [NavItems.CalculationUers]: `/calculation/prices`,
        [NavItems.CalculationRatios]: `/calculation/ratios`,
        [NavItems.CalculationStructure]: `/calculation/structure`,
        [NavItems.CalculationVor]: `/calculation/vor`,
        [NavItems.CalculationMtrs]: `/calculation/mtrs`,
        [NavItems.CalculationOther]: `/calculation/others`,
        [NavItems.CalculationPreview]: `/calculation/preview`,
        [NavItems.CalculationLimitedCosts]: `/calculation/limited`,
        [NavItems.CalculationLocalEstimate]: `/calculation/local-estimate`,
    };

    return urlMap[navLinkId] ? `${baseUrl}${urlMap[navLinkId]}/` : '';
};

export const ProjectTree = () => {
    const { project: projectRules } = useAccess();
    const { id, methodology } = useProjectParams();
    const { projectData, refreshProject } = useProjectData(id, false);

    const { state }: { state: { contractId?: number; refreshTree?: boolean } } = useLocation();

    const statusProject = projectData?.status;

    const isAvailableWorkOrder = !!projectData?.contractNavigation?.data?.workOrderNavs?.length;

    const navigate = useNavigate();

    const handleProjectClick = useCallback(() => {
        if (id && methodology) {
            navigate(`/projects/${methodology}/${id}/`);
        }
    }, [id, methodology, navigate]);

    useEffect(() => {
        if (state?.contractId || state?.refreshTree) {
            refreshProject();
        }
    }, [state?.contractId, state?.refreshTree, refreshProject]);

    const checkEntityIsActive = useCheckActiveTreeItem();

    return (
        <div id="projectTree" style={{ '--header-height': `${HEADER_HEIGHT + 24}px` } as CSSProperties}>
            <ProjectTreeLeaf
                label="Проект"
                horizontalSpace="m"
                onClick={handleProjectClick}
                rightSide={
                    <KebabTreeProject
                        projectId={id}
                        methodology={methodology}
                        projectRules={projectRules}
                        statusProject={statusProject}
                        isAvailableWorkOrder={isAvailableWorkOrder}
                    />
                }
                isActiveLink={checkEntityIsActive({
                    entityName: methodology ?? '',
                    entityId: [id ?? '', `${id}/edit`],
                })}
                boldStyle
            />
            <CalculationTree getContractNavItemUrl={getContractNavItemUrl} />
            <ContractTree getContractNavItemUrl={getContractNavItemUrl} />
        </div>
    );
};

ProjectTree.displayName = 'ProjectTree';
